<template>
  <div class="account-statistics-page">
    <BasePreloader v-if="pendingMain || pending"/>

    <ErrorView v-if="error"
               :code="error.code"
               :title="error.message"/>

    <template v-else-if="cellsData && receivedData">
      <div class="account-statistics-page__head">
        <div class="account-statistics-page__header-wrap">
          <h1 class="account-statistics-page__title">{{ $tStatistic('Statistic') }}</h1>

          <button
            class="d-md-block d-none base-btn base-btn--small mt-2"
            style="color: #0074ed; height: max-content"
            @click="exportData"
          >
            <BaseSvg name="ico-exit-out" width="1.7rem" height="1.5rem" />
            <span>{{ $tStatistic('Data export') }}</span>
          </button>
        </div>

        <div>
          <div class="d-md-none d-flex justify-flex-end mb-2">
            <button class="base-btn base-btn--small" style="color: #0074ed" @click="exportData">
              <BaseSvg name="ico-exit-out" width="1.7rem" height="1.5rem" />
              <span>{{ $tStatistic('Data export') }}</span>
            </button>
          </div>

          <div class="account-statistics-page__filter-sort-wrap d-flex items-center">
            <BaseMenu
              ref="filtersMenu"
              v-if="isModerator"
              :card-style="filtersMenuStyle"
            >
              <template v-slot:activator="{ on }">
                <VacanciesResponsesFilterSelector
                  :value="$tStatistic('Alpha links filter')"
                  style="width: 26rem; border: 1px solid black"
                  :filters-count="selectedAlphaLinks.length"
                  v-on="on"
                  @clear="clearSelectedAlphaLinks"
                />
              </template>

              <BaseInput
                v-model="filtersSearch"
                :label="$tStatistic('Search link')"
                class="mb-4"
                @input="onAlphaLinkSearchInput"
              />
              <div class="relative">
                <BasePreloader v-if="pendingSearchAlphaLinks" />
                <BaseCheckboxesList
                  v-model="selectedAlphaLinks"
                  :list="alphaLinks"
                  label-key="label"
                  color="#3674ED"
                  class="account-statistics-page__menu-inputs-list"
                  @input="onAlphaLinksSelect"
                >
                  <template v-if="!alphaLinks.length" v-slot:prepend>
                    <div>{{ $tStatistic('Alpha links not found') }}</div>
                  </template>
                </BaseCheckboxesList>
              </div>
              <button
                class="base-btn base-btn--outline-dark base-btn--x-small mt-4" style="width: 100%;"
                @click="clearSelectedAlphaLinks"
              >
                {{ $tMyResponses('Reset') }}
              </button>
            </BaseMenu>

            <StatisticPageDatesRange class="account-statistics-page__date-range"/>
          </div>
        </div>
      </div>

      <StatisticPageTable :key="counter"
                          :cells="cellsData"
                          :rows="rowsSorted"
                          :currentSort="currentSort"
                          :total="receivedData.total"
                          @sort="onSort"/>
    </template>
  </div>
</template>

<script>
  // import {random} from 'lodash'
  import {CancelToken, isCancel} from 'axios'
  import { mapGetters } from 'vuex';
  import {http} from '@/axios'
  import handleError from '@/helpers/handleError'
  import ErrorView from '@/components/ErrorView'
  import StatisticPageDatesRange from "@/components/statistic/StatisticsPageDatesRange";
  import StatisticPageTable from "@/components/statistic/StatisticsPageTable";
  import VacanciesResponsesFilterSelector from '../../components/vacanciesResponses/VacanciesResponsesFilterSelector';
  import { downloadByLink } from '../../helpers/download';
  import windowSizeMixin from '../../mixins/windowSizeMixin';

  const filterConstants = {
    FILTER_DATE_FROM: 'date_from',
    FILTER_DATE_TO: 'date_to',
    FILTER_PARTNER: 'partner',
  }

  const formatDate = new Intl.DateTimeFormat('uk', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  const PARTNER_CELL_KEY = 'partner'

  const DEFAULT_DATE_RANGE = getDefaultDateRangeFromDate(new Date())
  const DATE_RANGE_TYPE_KEY = 'date_type'

  function getDefaultDateRangeFromDate(current) {
    const from = new Date(current.getFullYear() - 1, current.getMonth(), current.getDate())
    const to = new Date(current.getFullYear(), current.getMonth(), current.getDate())

    return [formatDate.format(from), formatDate.format(to)]
  }

  let searchAlphaLinkTimeout = null;

  export default {
    name: 'AccountStatisticsPage',
    components: {
      ErrorView,
      StatisticPageDatesRange,
      StatisticPageTable,
      VacanciesResponsesFilterSelector,
    },
    provide() {
      const statisticPage = {};

      Object.defineProperty(statisticPage, 'additionalQueryParams', {
        enumerable: true,
        get: () => this.tableAdditionalQueryParams,
      });

      return {
        ...filterConstants,

        DEFAULT_DATE_RANGE,
        PARTNER_CELL_KEY,
        DATE_RANGE_TYPE_KEY,

        formatDate,

        statisticPage,
      };
    },
    mixins: [windowSizeMixin],
    data() {
      return {
        pendingMain: false,
        pending: false,
        cellsData: null,
        receivedData: null,
        cancel: null,

        ...filterConstants,

        currentSort: {
          key: null,
          toTop: false
        },
        error: null,

        counter: 1,

        alphaLinks: [],
        selectedAlphaLinks: [],
        filtersSearch: '',
        pendingSearchAlphaLinks: false,
      }
    },
    computed: {
      ...mapGetters('user', ['isModerator']),

      rowsSorted() {
        if (!this.currentSort.key) {
          return this.receivedData.rows
        }

        const {
          key,
          toTop
        } = this.currentSort

        function sort1(a, b) {
          return a[key] - b[key]
        }

        function sort2(a, b) {
          return b[key] - a[key]
        }

        return [...this.receivedData.rows].sort(toTop ? sort2 : sort1)
      },
      selectedDateRangeType() {
        return this.$route.query[this.DATE_RANGE_TYPE_KEY]
      },
      filtersMenuStyle() {
        return {
          width: '120%',
          top: 0,
          transform: this.mdOrDown ? null : 'translate(0, -30%)',
          maxWidth: 'calc(100vw - 4%)',
          padding: '2rem 2.2rem',
          minWidth: 'unset',
        };
      },
      tableAdditionalQueryParams() {
        return this.selectedAlphaLinks.length ? { alpha: this.selectedAlphaLinks.map(l => l.id) } : {};
      },
    },
    methods: {
      onAlphaLinkSearchInput() {
        if (searchAlphaLinkTimeout) clearTimeout(searchAlphaLinkTimeout);

        searchAlphaLinkTimeout = setTimeout(() => {
          this.loadAlphaLinks(this.filtersSearch);
          searchAlphaLinkTimeout = null;
        }, 800);
      },
      setAlphaLinksFromQueryParams() {
        const links = [];
        const { alpha } = this.$route.query;
        if (!alpha) return;

        const queryLinksIds = Array.isArray(alpha) ? alpha : [alpha];

        this.alphaLinks.forEach(link => {
          if (queryLinksIds.some(id => Number(id) === link.id)) links.push(link);
        });
        this.selectedAlphaLinks = links;
      },
      async loadAlphaLinks(q) {
        if (!this.isModerator) return;

        this.pendingSearchAlphaLinks = true;
        try {
          const params = q ? { q } : {};
          const { data } = await http.get('/api/cabinet/agent/stats/get-alpha-filter', { params });
          this.alphaLinks = data.data;
        } catch {
          this.$vModal.open('warning', { title: 'Failed to load alpha links' });
        } finally {
          this.pendingSearchAlphaLinks = false;
        }
      },
      onAlphaLinksSelect() {
        const alpha = this.selectedAlphaLinks.map(l => l.id);
        const query = { ...this.$route.query, alpha };
        this.$router.replace({ query });
        this.load();
      },
      clearSelectedAlphaLinks() {
        this.selectedAlphaLinks = [];
        this.onAlphaLinksSelect();
      },

      onSort(key) {
        if (this.currentSort.key === key) {
          this.currentSort.toTop = !this.currentSort.toTop
        } else {
          this.currentSort.key = key
          this.currentSort.toTop = true
        }
      },
      loadCells() { //titles of tables
        return http.get('/api/cabinet/agent/stats/cells')
            .then(({data}) => {
              this.cellsData = data.data
            })
            .catch(e => {
              this.error = {
                code: e.response && e.response.status,
                message: handleError(e)
              }
            })
      },
      load() {
        if (this.cancel) {
          this.cancel()
          this.cancel = null
        }

        this.pending = true
        this.error = null

        const params = this.getRequestQueryParams();

        return http.get('/api/cabinet/agent/stats/rows', {
          params,
          cancelToken: new CancelToken((c) => {
            this.cancel = c;
          })
        }).then(({data}) => {
          this.receivedData = data.data;
          this.counter++;
          this.pending = false;
        }).catch(e => {
          if (isCancel(e)) {
            return
          }

          this.receivedData = null

          this.error = {
            code: e.response && e.response.status,
            message: handleError(e)
          }

          this.pending = false
        })
      },
      getRequestQueryParams() {
        const query = this.$route.query
        const params = {}

        for (const key in filterConstants) {
          const paramKey = filterConstants[key]

          if (paramKey === filterConstants.FILTER_PARTNER && !Array.isArray(query[paramKey])) {
            params[paramKey] = [query[paramKey]];
            continue;
          }

          params[paramKey] = query[paramKey]
        }

        if (this.selectedDateRangeType !== 'all') {
          if (!params[filterConstants.FILTER_DATE_FROM]) {
            params[filterConstants.FILTER_DATE_FROM] = DEFAULT_DATE_RANGE[0]
          }

          if (!params[filterConstants.FILTER_DATE_TO]) {
            params[filterConstants.FILTER_DATE_TO] = DEFAULT_DATE_RANGE[1]
          }
        }

        const alphaFilters = this.$route.query.alpha;
        if (alphaFilters?.length) {
          params.alpha = alphaFilters;
        }

        return params;
      },
      async exportData() {
        try {
          const { data } = await http.get('/api/cabinet/agent/stats/get-csv', { params: this.getRequestQueryParams() });
          downloadByLink(data.url);
        } catch (e) {
          this.$vModal.open('warning', { title: 'Failed to load file' });
        }
      },
    },
    async created() {
      this.pendingMain = true
      await this.loadAlphaLinks();
      this.setAlphaLinksFromQueryParams();

      await this.loadCells()
      Promise.all([

        this.load(),
      ]).finally(() => {
        this.pendingMain = false
      })

      createMapRouteQueryWatchers(
          this,
          Object.values(filterConstants),
          () => {
            this.load()
          }
      )
    }
  }

  function createMapRouteQueryWatchers(vm, keys, callBack) {
    keys.forEach(key => {
      vm.$watch(
          function () {
            return vm.$route.query[key]
          },
          callBack
      )
    })
  }
</script>

<style lang="less">

</style>
